import {
  addActionBarDropdownMenuItem,
  registerBulkAction,
  ModalService,
  DataService,
  NotificationService,
  addActionBarItem,
  registerHistoryEntryComponent,
  PageLocationId,
} from '@vendure/admin-ui/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Mutation,
  MutationImportProductsFromCsvArgs,
  MutationExportProductsToCsvArgs,
  MutationGenProductReviewsCsvTemplateArgs,
  MutationImportProductReviewsFromCsvArgs,
} from '../common/ui/generated';

import {
  IMPORT_PRODUCTS_FROM_CSV,
  EXPORT_PRODUCTS_TO_CSV,
  GENERATE_PRODUCT_REVIEWS_TO_CSV,
  IMPORT_PRODUCT_REVIEWS_FROM_CSV,
  PRODUCT_EXPORT_CSV_TEMPLATE_DOWNLOAD_URL,
} from './graphql';
import { lastValueFrom } from 'rxjs';
import { FulfillmentUpdateHistoryEntryComponent } from './components/fulfillment-update-history-entry.component';
import { downloadFile } from '../common/ui/utils';

enum ImportProductType {
  Product = 'product',
  ProductReview = 'productReview',
}

/**
 * Vendure `PageLocationId` is a closed union in v3.1.x, so custom extension routes
 * (like `/extensions/product-reviews`) aren't included at the type level.
 * We still want to target that action bar location at runtime.
 */
const PRODUCT_REVIEWS_LOCATION_ID = 'product-reviews' as unknown as PageLocationId;

async function refreshListView(route: ActivatedRoute, router: Router): Promise<void> {
  const originalPerPageRaw = (route.snapshot.queryParams as any)?.perPage;
  const originalPerPage = originalPerPageRaw != null ? Number(originalPerPageRaw) : undefined;
  const currentPerPage = Number.isFinite(originalPerPage) ? (originalPerPage as number) : 10;
  const bumpPerPage = currentPerPage + 1;

  console.log('[import-export] refreshListView start', {
    url: router.url,
    currentPerPage,
    originalPerPage,
    bumpPerPage,
  });

  // Vendure list pages refetch when `page` or `perPage` changes. We don't have access to the
  // list component instance here, so we force a refetch by temporarily bumping `perPage`
  // and then restoring it.
  await router.navigate([], {
    relativeTo: route,
    queryParams: { perPage: bumpPerPage },
    queryParamsHandling: 'merge',
    skipLocationChange: true,
  });

  await router.navigate([], {
    relativeTo: route,
    queryParams: { perPage: originalPerPage ?? null },
    queryParamsHandling: 'merge',
    skipLocationChange: true,
  });

  console.log('[import-export] refreshListView done', {
    url: router.url,
    restoredPerPage: originalPerPage ?? null,
  });
}



async function exportProducts(action: string, productIds: string[], dataService: DataService, notificationService: NotificationService) {
  // const dataService = context.injector.get(DataService);
  dataService.mutate<
    Mutation,
    MutationExportProductsToCsvArgs
  >(
    EXPORT_PRODUCTS_TO_CSV, { input: { action, productIds } }
  ).subscribe({
    next: (response: Mutation) => {
      if (response.exportProductsToCsv.error) {
        notificationService.error(response.exportProductsToCsv.error);
      } else {
        downloadFile(`products-${new Date().toISOString()}.csv`, response.exportProductsToCsv.downloadURL);
        notificationService.info(`Export done`);
      }
    },
    error: (error: any) => {
      console.error(`Error during export:${error}`);
      notificationService.error(`Error during export ${error.message}`);
    },
    complete: () => {
      console.debug('File export complete');
    },
  });
}

async function importProducts(
  file: File,
  dataService: DataService,
  notificationService: NotificationService,
  onSuccess?: () => unknown,
) {

  // const dataService = context.injector.get(DataService);
  dataService.mutate<
    Mutation,
    MutationImportProductsFromCsvArgs
  >(
    IMPORT_PRODUCTS_FROM_CSV, { file }
  ).subscribe({
    next: (response: Mutation) => {
      if (response.importProductsFromCsv.error) {
        notificationService.error(response.importProductsFromCsv.error);
      } else {
        notificationService.info(`Import done, processed: ${response.importProductsFromCsv.processedProducts}  imported ${response.importProductsFromCsv.importedProducts} product(s)`);
        Promise.resolve(onSuccess?.()).catch((e: any) => {
          console.error('Failed to refresh after import', e);
        });
      }
    },
    error: (error: any) => {
      console.error('Error during file upload:', error);
      notificationService.error(`Error during file upload ${error.message}`);
    },
    complete: () => {
      console.debug('File upload complete');
    },
  });
}

async function onProductImport(
  importType: ImportProductType,
  dataService: DataService,
  notificationService: NotificationService,
  onSuccess?: () => unknown,
) {
  // Create a hidden file input element programmatically
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.style.display = 'none';  // Ensure it stays hidden
  fileInput.accept = ".csv"

  // Append the input to the document body
  document.body.appendChild(fileInput);

  fileInput.addEventListener('change', async (event: Event) => {
    try {
      const input = event.target as HTMLInputElement;
      if (input.files && input.files.length > 0) {
        const file = input.files[0];
        if (importType === ImportProductType.Product) {
          await importProducts(file, dataService, notificationService, onSuccess);
        } else if (importType === ImportProductType.ProductReview) {
          await importProductReviews(file, dataService, notificationService);
        } else {
          notificationService.error('Unknown import type');
        }
      }
    } catch (error: any) {
      notificationService.error('Error executing import: ' + error.message);
    }
  });

  // Programmatically click the file input to open the file dialog
  fileInput.click();

  // Remove the input after file selection to clean up
  fileInput.remove();
}


async function importProductReviews(file: File, dataService: DataService, notificationService: NotificationService) {

  dataService.mutate<
    Mutation,
    MutationImportProductReviewsFromCsvArgs
  >(
    IMPORT_PRODUCT_REVIEWS_FROM_CSV, { file }
  ).subscribe({
    next: (response: Mutation) => {
      if (response.importProductReviewsFromCsv.error) {
        notificationService.error(response.importProductReviewsFromCsv.error);
      } else {
        notificationService.info(`Import done, added: ${response.importProductReviewsFromCsv.importedProductReviews} review(s)`);
      }
    },
    error: (error: any) => {
      console.error('Error during file upload:', error);
      notificationService.error(`Error during file upload ${error.message}`);
    },
    complete: () => {
      console.debug('File upload complete');
    },
  });
}

async function onProductExportTemplate(_action: string, _productIds: string[], dataService: DataService, notificationService: NotificationService) {
  dataService.query<any>(PRODUCT_EXPORT_CSV_TEMPLATE_DOWNLOAD_URL).mapSingle(data => data.productExportCsvTemplateDownloadUrl).subscribe({
    next: (result: any) => {
      if (result.error) {
        notificationService.error(result.error);
      } else {
        downloadFile('product-export-template.csv', result.downloadURL);
        notificationService.info('Export Product Template Done');
      }
    },
    error: (error: any) => {
      console.error(`Error during export:${error}`);
      notificationService.error(`Error during export ${error.message}`);
    },
    complete: () => {
      console.debug('File export complete');
    },
  });
}

async function onProductReviewExportTemplate(action: string, productIds: string[], dataService: DataService, notificationService: NotificationService) {
  // const dataService = context.injector.get(DataService);
  dataService.mutate<
    Mutation,
    MutationGenProductReviewsCsvTemplateArgs
  >(
    GENERATE_PRODUCT_REVIEWS_TO_CSV, { input: { action, productIds } }
  ).subscribe({
    next: (response: Mutation) => {
      if (response.genProductReviewsCsvTemplate.error) {
        notificationService.error(response.genProductReviewsCsvTemplate.error);
      } else {
        downloadFile('exported-products-reviews.csv', response.genProductReviewsCsvTemplate.downloadURL)
        notificationService.info(`Export Products Reviews Template Done`);
      }
    },
    error: (error: any) => {
      console.error(`Error during export:${error}`);
      notificationService.error(`Error during export ${error.message}`);
    },
    complete: () => {
      console.debug('File export complete');
    },
  });
}



function assetsToCsv(assets: any[]): string {
  const header = ['ID', 'FileName', 'FileSize', 'Width', 'Height', 'Source', 'Preview'];
  const rows = assets.map(asset => [
    asset.id,
    `"${asset.name}"`,
    `"${asset.fileSize}"`,
    `"${asset.width}"`,
    `"${asset.height}"`,
    `"${asset.source}"`,
    `"${asset.preview}"`,
  ]);
  return [header.join(','), ...rows.map(row => row.join(','))].join('\n');
}
function triggerDownload(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

export default [
  addActionBarDropdownMenuItem({
    id: 'ch-import-product',
    label: 'Import Products',
    locationId: 'product-list',
    icon: 'import',
    requiresPermission: 'UpdateProduct',
    hasDivider: true,
    onClick: async (event, context) => {
      const router = context.injector.get(Router);
      await onProductImport(ImportProductType.Product, context.dataService, context.notificationService, () => {
        return refreshListView(context.route, router);
      });
    },
  }),
  addActionBarDropdownMenuItem({
    id: 'ch-export-product',
    label: 'Export All Products',
    locationId: 'product-list',
    icon: 'export',
    requiresPermission: 'ReadProduct',
    onClick: async (event, context) => {
      const modalService = context.injector.get(ModalService);
      modalService
        .dialog({
          title: `Do you want to export all products?`,
          buttons: [
            { type: 'secondary', label: 'No' },
            { type: 'primary', label: 'Yes', returnValue: true },
          ],
        })
        .subscribe(async (response) => {
          if (response) {
            await exportProducts('export-all', [], context.dataService, context.notificationService);
          }
        });
    },
  }),
  addActionBarDropdownMenuItem({
    id: 'ch-export-product-template',
    label: 'Download Product Export Template',
    locationId: 'product-list',
    icon: 'export',
    requiresPermission: 'ReadProduct',
    onClick: async (event, context) => {
      await onProductExportTemplate('export-all', [], context.dataService, context.notificationService);
    },
  }),
  registerBulkAction({
    location: 'product-list',
    label: 'Export Products',
    icon: 'export',
    requiresPermission: 'ReadProduct',
    onClick: async (context) => {
      const modalService = context.injector.get(ModalService);
      modalService
        .dialog({
          title: `Export ${context.selection.length} products ?`,
          buttons: [
            { type: 'secondary', label: 'cancel' },
            { type: 'primary', label: 'OK', returnValue: true },
          ],
        })
        .subscribe(async (response) => {
          if (response) {
            const productIds = context.selection.map(item => item.id);
            const dataService = context.injector.get(DataService);
            const notificationService = context.injector.get(NotificationService);
            await exportProducts('export-bulk', productIds, dataService, notificationService);
          }
        });
    },
  }),
  addActionBarItem({
    id: 'ch-import-product-reviews',
    label: 'Import Product Reviews',
    locationId: PRODUCT_REVIEWS_LOCATION_ID,
    icon: 'import',
    requiresPermission: 'UpdateProduct',
    onClick: async (event, context) => {
      const router = context.injector.get(Router);
      await onProductImport(ImportProductType.ProductReview, context.dataService, context.notificationService, () => {
        return refreshListView(context.route, router);
      });
    },
  }),
  addActionBarDropdownMenuItem({
    id: 'ch-export-product-reviews-template',
    label: 'Download Product Reviews Template',
    locationId: PRODUCT_REVIEWS_LOCATION_ID,
    icon: 'export',
    requiresPermission: 'ReadProduct',
    onClick: async (event, context) => {
      await onProductReviewExportTemplate('export-all', [], context.dataService, context.notificationService);
    },
  }),
  addActionBarItem({
    id: 'asset-simple-list-export',
    locationId: 'product-list',
    label: 'Export Asset Simple CSV',
    icon: 'export',
    requiresPermission: 'SuperAdmin',
    onClick: async (event, context) => {
      const dataService = context.injector.get(DataService);
      const notificationService = context.injector.get(NotificationService);
      try {
        const allAssets: any[] = [];
        const take = 100;
        let currentPage = 1;
        let totalItems: number;
        let currentItems = 0;
        do {
          const result = await lastValueFrom(
            dataService.product.getAssetList(
              take,
              (currentPage - 1) * take,
            ).single$,
          );
          allAssets.push(...result.assets?.items || []);
          currentItems = result.assets.items.length;
          totalItems = result.assets.totalItems;
          currentPage++;
          console.debug('Fetched page', result);
        } while (currentItems === take);

        if (allAssets.length === 0) {
          notificationService.info('There are no assets to export.');
          return;
        }
        const csvString = assetsToCsv(allAssets);
        triggerDownload(csvString, `assets-simple-export-${new Date().toISOString().slice(0, 10)}.csv`);
        notificationService.success(`Successfully exported ${allAssets.length} assets.`);
      } catch (e: any) {
        notificationService.error('An error occurred while exporting assets.');
        console.error(e);
      }
    }
  }),
  registerHistoryEntryComponent({
    type: 'ORDER_FULFILLMENT_UPDATE',
    component: FulfillmentUpdateHistoryEntryComponent,
}),
];